import { FC, SyntheticEvent, useState } from 'react';
import { notificationTypes } from '../../../constants';
import { useAppDispatch } from '../../../hooks';
import { sendNotification } from '../../../store/slices';
import { ICreateNotification, IUser, NotificationOption } from '../../../types';

const inputClass = 'bg-subtle border border-slate-medium rounded-lg px-3 py-3 text-sm text-text placeholder:text-text-placeholder focus:outline-none focus:bg-white focus:border-primary w-full';

interface INotifications {
  toggle: () => void;
  selectedRows: IUser[];
  singleUser?: boolean;
}

export const SendNotifications: FC<INotifications> = ({
  toggle,
  selectedRows,
  singleUser,
}) => {
  const dispatch = useAppDispatch();

  const [activeType, setActiveType] = useState<NotificationOption>('push');
  const [payload, setPayload] = useState<ICreateNotification>({
    content: '',
    subject: '',
  });

  const typeValueMapper = {
    sms: 'phoneNumber',
    email: 'email',
    push: 'pushNotifications',
  };

  const typeKeyMapper = {
    sms: 'phones',
    email: 'emails',
    push: 'receivers',
  };

  const onGetInputValue = (
    event: SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.currentTarget.value;
    const name = event.currentTarget.name;
    setPayload({ ...payload, [name]: value });
  };

  const returnReceiver = (selectedRows: IUser[]) => {
    const receiverKey = typeKeyMapper[activeType as keyof typeof typeKeyMapper];
    const receiverValue =
      activeType !== 'push' &&
      typeValueMapper[activeType as keyof typeof typeValueMapper];

    let value: string[] = [];
    for (let i = 0; i < selectedRows.length; i++) {
      if (selectedRows[i]) {
        if (activeType !== 'push') {
          const getValue = selectedRows[i][receiverValue as keyof IUser]!.toString();
          value.push(getValue);
        } else {
          selectedRows[i].pushNotifications.forEach((object) => {
            for (let key in object) {
              value.push(object[key as keyof typeof object]);
            }
          });
        }
      }
    }
    return { [receiverKey]: value };
  };

  const _sendNotifications = (e?: SyntheticEvent<HTMLFormElement>) => {
    if (!(payload.content.length > 0 && Object.keys(selectedRows).length > 0)) return;
    e?.preventDefault();
    const receiver = returnReceiver(selectedRows);
    dispatch(sendNotification({ payload: { ...receiver, ...payload }, cb: toggle, type: activeType }));
  };

  const sendToAll = () => {
    dispatch(sendNotification({ sendToAll: true, payload: { ...payload }, cb: toggle, type: activeType }));
  };

  return (
    <div className="bg-white p-6 rounded-2xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="bold text-xl text-text">Send notification</h2>
        <button onClick={toggle} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors">
          <svg width={16} height={16} viewBox="0 0 16 16" fill="none">
            <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
      </div>

      <form onSubmit={_sendNotifications}>
        {/* Type toggles */}
        <div className="flex gap-2 mb-5">
          {notificationTypes.map(({ name, value }) => (
            <button
              key={value}
              type="button"
              onClick={() => setActiveType(value as NotificationOption)}
              className={`px-4 py-2 text-sm medium rounded-lg border transition-colors ${
                activeType === value
                  ? 'border-primary text-primary bg-primary-medium'
                  : 'border-slate-medium text-text hover:bg-gray-50'
              }`}
            >
              {name.toUpperCase()}
            </button>
          ))}
        </div>

        {/* Subject */}
        {activeType !== 'sms' && (
          <div className="mb-4">
            <label className="medium text-sm text-text block mb-1.5">Subject</label>
            <input
              type="text"
              name="subject"
              value={payload.subject}
              onChange={onGetInputValue}
              placeholder="Enter subject"
              required
              className={inputClass}
            />
          </div>
        )}

        {/* Description */}
        <div className="mb-6">
          <label className="medium text-sm text-text block mb-1.5">Description</label>
          <textarea
            name="content"
            value={payload.content}
            onChange={onGetInputValue}
            placeholder="Enter description"
            required
            rows={4}
            className={`${inputClass} resize-none`}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={toggle} className="px-6 py-2.5 text-sm medium border border-slate-medium rounded-lg hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          {Object.keys(selectedRows).length > 0 && (
            <button
              type="submit"
              disabled={!(payload.content.length > 0)}
              className={`px-6 py-2.5 text-sm medium rounded-lg text-white transition-colors ${
                payload.content.length > 0 ? 'bg-primary hover:opacity-90' : 'bg-primary-disabled cursor-not-allowed'
              }`}
            >
              Send notification
            </button>
          )}
          {!singleUser && (
            <button
              type="button"
              onClick={sendToAll}
              disabled={!(payload.content.length > 0)}
              className={`px-6 py-2.5 text-sm medium rounded-lg text-white transition-colors ${
                payload.content.length > 0 ? 'bg-primary hover:opacity-90' : 'bg-primary-disabled cursor-not-allowed'
              }`}
            >
              Send to all
            </button>
          )}
        </div>
      </form>
    </div>
  );
};
